import { Collapse, CollapseProps, Flex, List, Space, Statistic, Typography, theme } from 'antd';
import { FC } from 'react';
import { CheckCircle, ExclamationTriangle } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { AppRoutes } from '@/common/interfaces';

import { SiteGroupSearchAnalytics, SiteGroupSearchAnalyticsSkippedDomain } from '@/modules/SiteGroups';

const DomainTitle: FC<{ domain: Pick<SiteGroupSearchAnalyticsSkippedDomain, 'id' | 'name' | 'siteId' | 'siteName'> }> = ({ domain }) => (
  <Space size={ 4 } wrap>
    {
      domain.id
        ? (
          <Link to={ AppRoutes.SiteDomainsById.replace(':siteId', domain.siteId).replace(':domainId', domain.id) }>
            { domain.name }
          </Link>
        )
        : '—'
    }
    <Typography.Text type="secondary">({ domain.siteName })</Typography.Text>
  </Space>
);

interface Props {
  responseAggregationType: SiteGroupSearchAnalytics['responseAggregationType'];
  domains: SiteGroupSearchAnalytics['domains'];
}

export const SiteGroupsSearchAnalyticsDomainsView: FC<Props> = ({ responseAggregationType, domains }) => {
  const { token } = theme.useToken();

  const collapseItems: CollapseProps['items'] = [];

  if (domains.analysed.length > 0) {
    collapseItems.push({
      key: 'analysed',
      label: (
        <Space>
          <CheckCircle color={ token.colorSuccess }/>
          Проаналізовані домени ({ domains.analysed.length })
        </Space>
      ),
      children: (
        <List
          size="small"
          style={{ maxHeight: 320, overflowY: 'auto' }}
          dataSource={ domains.analysed }
          renderItem={ (domain) => (
            <List.Item key={ domain.siteId } style={{ paddingInline: 0 }}>
              <DomainTitle domain={ domain }/>
            </List.Item>
          ) }
        />
      )
    });
  }

  if (domains.skipped.length > 0) {
    collapseItems.push({
      key: 'skipped',
      label: (
        <Space>
          <ExclamationTriangle color={ token.colorWarning }/>
          Пропущені домени ({ domains.skipped.length })
        </Space>
      ),
      children: (
        <List
          size="small"
          style={{ maxHeight: 320, overflowY: 'auto' }}
          dataSource={ domains.skipped }
          renderItem={ (domain) => (
            <List.Item key={ domain.siteId } style={{ paddingInline: 0 }}>
              <List.Item.Meta title={ <DomainTitle domain={ domain }/> } description={ domain.reason }/>
            </List.Item>
          ) }
        />
      )
    });
  }

  return (
    <Flex vertical gap={ token.margin }>
      <Flex wrap gap={ token.marginXL }>
        <Statistic
          title="Проаналізовано доменів"
          value={ domains.analysed.length }
          valueStyle={{ color: token.colorSuccess }}
        />
        <Statistic
          title="Пропущено доменів"
          value={ domains.skipped.length }
          valueStyle={ domains.skipped.length > 0 ? { color: token.colorWarning } : undefined }
        />
        { responseAggregationType && <Statistic title="Тип збирання" value={ responseAggregationType }/> }
      </Flex>

      { collapseItems.length > 0 && <Collapse size="small" items={ collapseItems }/> }
    </Flex>
  );
};
